import { StatusKey } from '@/Enums/Clients/StatusKey.enum';
import SearchInput from '@/components/SearchInput';
import StatusCheckbox from '@/components/StatusCheckbox';
import { CLIENTS_STATUS_LABELS, STATUS_FILTER_MAP } from '@/constants/clients';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import FilterPopover from '@/pages/Clients/ClientsGrid/components/FilterPopover';
import { useAppDispatch } from '@/store/hooks';
import { setFilters } from '@/store/slices/clients.slice';
import { Button } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, useCallback, useState } from 'react';

interface IProps {
  total: number;
  handleCreationModalOpen: () => void;
}

const Toolbar = ({ total, handleCreationModalOpen }: IProps) => {
  const dispatch = useAppDispatch();

  const [statusFilter, setStatusFilter] = useState<StatusKey>(StatusKey.ALL);
  const [search, setSearch] = useState<string>('');

  const handleStatusFilterChange = useCallback(
    (value: StatusKey) => {
      setStatusFilter(value);
      const isActive = STATUS_FILTER_MAP[value] ?? null;
      dispatch(setFilters({ isActive }));
    },
    [dispatch, setStatusFilter],
  );

  const updateSearchFilter = useCallback(
    debounce((newSearch: string) => {
      dispatch(setFilters({ search: newSearch }));
    }, 500),
    [dispatch],
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearch = event.target?.value;
      setSearch(newSearch);
      updateSearchFilter(newSearch);
    },
    [updateSearchFilter, setSearch],
  );

  return (
    <GridToolbarContainer sx={{ padding: 0 }}>
      <div className='p-[20px] relative w-full'>
        <div className='flex justify-between items-center'>
          <div className='mb-[10px]'>
            <h2 className='mb-[5px]'>
              <span className='text-[#171725] text-[24px] font-semibold'>Clients</span>
              <span className='text-[#C1BFC2] text-[20px]'>
                &nbsp;({numberToUsFormat(total, { maximumFractionDigits: 0 })})
              </span>
            </h2>
          </div>
          <div>
            <Button
              sx={{ height: 40, width: 200 }}
              color='primary'
              variant='contained'
              onClick={handleCreationModalOpen}
            >
              + Create New Client
            </Button>
          </div>
        </div>
        <div className='flex justify-between'>
          <div className='flex gap-[6px]'>
            {CLIENTS_STATUS_LABELS.map(({ name, label }) => (
              <StatusCheckbox
                key={name}
                label={label}
                onChange={() => handleStatusFilterChange(name)}
                name={name}
                checked={statusFilter === name}
              />
            ))}
          </div>
          <div className='table-controls flex gap-[10px]'>
            <SearchInput placeholder='Search..' value={search} onChange={onSearchChange} />
            <FilterPopover />
          </div>
        </div>
      </div>
    </GridToolbarContainer>
  );
};

export default Toolbar;
